import type { TBusinessType } from '@/interfaces/IStoreSetup';

/** The 17 codes in chip order (API `business-type.const.ts`). */
export const BUSINESS_TYPES: readonly TBusinessType[] = [
  'restaurant',
  'grill',
  'pizza',
  'burger',
  'cafe',
  'bakery',
  'icecream',
  'bar',
  'asian',
  'shop',
  'other',
  'mexican',
  'healthy',
  'seafood',
  'takeaway',
  'delivery_only',
  'foodtruck',
];

/** The 11 chips visible before «Ver más». */
export const PRIMARY_BUSINESS_TYPES: readonly TBusinessType[] = BUSINESS_TYPES.slice(
  0,
  11
);

export function isBusinessType(value: unknown): value is TBusinessType {
  return BUSINESS_TYPES.some((code) => code === value);
}

/**
 * Google Places (New) types → business code. `foodtruck` and `other` are never
 * prefilled. Every key is a Table A type of Places (New) (the spec checks them
 * against `__fixtures__/google-place-types.json`). Types that are not listed and
 * end in `restaurant` or `food` read as a restaurant; the few explicit
 * `restaurant` entries keep a later, broader type (a bar, a store) from winning.
 */
export const PLACE_TYPE_MAP: ReadonlyMap<string, TBusinessType> = new Map<
  string,
  TBusinessType
>([
  ['steak_house', 'grill'],
  ['argentinian_restaurant', 'grill'],
  ['barbecue_restaurant', 'grill'],
  ['brazilian_restaurant', 'grill'],
  ['pizza_restaurant', 'pizza'],
  ['pizza_delivery', 'pizza'],
  ['hamburger_restaurant', 'burger'],
  ['fast_food_restaurant', 'burger'],
  ['hot_dog_restaurant', 'burger'],
  ['hot_dog_stand', 'burger'],
  ['cafe', 'cafe'],
  ['coffee_shop', 'cafe'],
  ['coffee_roastery', 'cafe'],
  ['coffee_stand', 'cafe'],
  ['cat_cafe', 'cafe'],
  ['dog_cafe', 'cafe'],
  ['tea_house', 'cafe'],
  ['cafeteria', 'cafe'],
  ['snack_bar', 'cafe'],
  ['breakfast_restaurant', 'cafe'],
  ['brunch_restaurant', 'cafe'],
  ['bakery', 'bakery'],
  ['cake_shop', 'bakery'],
  ['dessert_shop', 'bakery'],
  ['pastry_shop', 'bakery'],
  ['confectionery', 'bakery'],
  ['chocolate_shop', 'bakery'],
  ['dessert_restaurant', 'bakery'],
  ['donut_shop', 'bakery'],
  ['bagel_shop', 'bakery'],
  ['ice_cream_shop', 'icecream'],
  ['bar', 'bar'],
  ['pub', 'bar'],
  ['irish_pub', 'bar'],
  ['gastropub', 'bar'],
  ['brewpub', 'bar'],
  ['beer_garden', 'bar'],
  ['wine_bar', 'bar'],
  ['cocktail_bar', 'bar'],
  ['lounge_bar', 'bar'],
  ['sports_bar', 'bar'],
  ['hookah_bar', 'bar'],
  ['brewery', 'bar'],
  ['bar_and_grill', 'bar'],
  ['night_club', 'bar'],
  ['sushi_restaurant', 'asian'],
  ['japanese_restaurant', 'asian'],
  ['japanese_curry_restaurant', 'asian'],
  ['japanese_izakaya_restaurant', 'asian'],
  ['ramen_restaurant', 'asian'],
  ['tonkatsu_restaurant', 'asian'],
  ['yakiniku_restaurant', 'asian'],
  ['yakitori_restaurant', 'asian'],
  ['chinese_restaurant', 'asian'],
  ['cantonese_restaurant', 'asian'],
  ['chinese_noodle_restaurant', 'asian'],
  ['dim_sum_restaurant', 'asian'],
  ['dumpling_restaurant', 'asian'],
  ['hot_pot_restaurant', 'asian'],
  ['noodle_shop', 'asian'],
  ['taiwanese_restaurant', 'asian'],
  ['mongolian_barbecue_restaurant', 'asian'],
  ['korean_restaurant', 'asian'],
  ['korean_barbecue_restaurant', 'asian'],
  ['thai_restaurant', 'asian'],
  ['vietnamese_restaurant', 'asian'],
  ['burmese_restaurant', 'asian'],
  ['cambodian_restaurant', 'asian'],
  ['filipino_restaurant', 'asian'],
  ['indonesian_restaurant', 'asian'],
  ['malaysian_restaurant', 'asian'],
  ['asian_restaurant', 'asian'],
  ['asian_fusion_restaurant', 'asian'],
  ['indian_restaurant', 'asian'],
  ['north_indian_restaurant', 'asian'],
  ['south_indian_restaurant', 'asian'],
  ['pakistani_restaurant', 'asian'],
  ['bangladeshi_restaurant', 'asian'],
  ['sri_lankan_restaurant', 'asian'],
  ['tibetan_restaurant', 'asian'],
  ['mexican_restaurant', 'mexican'],
  ['taco_restaurant', 'mexican'],
  ['burrito_restaurant', 'mexican'],
  ['tex_mex_restaurant', 'mexican'],
  ['vegan_restaurant', 'healthy'],
  ['vegetarian_restaurant', 'healthy'],
  ['salad_shop', 'healthy'],
  ['juice_shop', 'healthy'],
  ['acai_shop', 'healthy'],
  ['seafood_restaurant', 'seafood'],
  ['fish_and_chips_restaurant', 'seafood'],
  ['oyster_bar_restaurant', 'seafood'],
  ['meal_takeaway', 'takeaway'],
  ['meal_delivery', 'delivery_only'],
  ['grocery_store', 'shop'],
  ['asian_grocery_store', 'shop'],
  ['supermarket', 'shop'],
  ['discount_supermarket', 'shop'],
  ['hypermarket', 'shop'],
  ['convenience_store', 'shop'],
  ['general_store', 'shop'],
  ['market', 'shop'],
  ['farmers_market', 'shop'],
  ['butcher_shop', 'shop'],
  ['liquor_store', 'shop'],
  ['health_food_store', 'shop'],
  ['tea_store', 'shop'],
  ['candy_store', 'shop'],
  ['food_store', 'shop'],
  ['deli', 'shop'],
  ['store', 'shop'],
  ['bistro', 'restaurant'],
  ['diner', 'restaurant'],
  ['food_court', 'restaurant'],
  ['kebab_shop', 'restaurant'],
  ['sandwich_shop', 'restaurant'],
]);

/** One place type → a code, or null when the type says nothing about the business. */
function businessTypeOf(placeType: string): TBusinessType | null {
  const mapped = PLACE_TYPE_MAP.get(placeType);
  if (mapped) return mapped;
  if (placeType.endsWith('restaurant') || placeType.endsWith('food'))
    return 'restaurant';
  return null;
}

/** First match on `primaryType`, then on each of `types` in order; none → restaurant. */
export function businessTypeFromPlace(
  primaryType: string | null,
  types: string[]
): TBusinessType {
  const candidates = primaryType ? [primaryType, ...types] : types;
  for (const candidate of candidates) {
    const code = businessTypeOf(candidate);
    if (code) return code;
  }
  return 'restaurant';
}

export interface IDefaultOrderTypes {
  table: boolean;
  pickup: boolean;
  delivery: boolean;
}

const ALL_TYPES: IDefaultOrderTypes = {
  table: true,
  pickup: true,
  delivery: true,
};
const TABLE_PICKUP: IDefaultOrderTypes = {
  table: true,
  pickup: true,
  delivery: false,
};
const TABLE_ONLY: IDefaultOrderTypes = {
  table: true,
  pickup: false,
  delivery: false,
};
const PICKUP_DELIVERY: IDefaultOrderTypes = {
  table: false,
  pickup: true,
  delivery: true,
};
const PICKUP_ONLY: IDefaultOrderTypes = {
  table: false,
  pickup: true,
  delivery: false,
};
const DELIVERY_ONLY: IDefaultOrderTypes = {
  table: false,
  pickup: false,
  delivery: true,
};

const ORDER_TYPE_PRESETS: Readonly<
  Record<TBusinessType, IDefaultOrderTypes>
> = {
  pizza: ALL_TYPES,
  burger: ALL_TYPES,
  asian: ALL_TYPES,
  mexican: ALL_TYPES,
  restaurant: TABLE_PICKUP,
  grill: TABLE_PICKUP,
  cafe: TABLE_PICKUP,
  bakery: TABLE_PICKUP,
  healthy: TABLE_PICKUP,
  seafood: TABLE_PICKUP,
  other: TABLE_PICKUP,
  bar: TABLE_ONLY,
  icecream: PICKUP_DELIVERY,
  shop: PICKUP_DELIVERY,
  takeaway: PICKUP_ONLY,
  foodtruck: PICKUP_ONLY,
  delivery_only: DELIVERY_ONLY,
};

/** The «Pedidos» preset of a business type (§4.8); a fresh object on every call. */
export function defaultOrderTypes(code: TBusinessType): IDefaultOrderTypes {
  const preset = isBusinessType(code)
    ? ORDER_TYPE_PRESETS[code]
    : ORDER_TYPE_PRESETS.restaurant;
  return { ...preset };
}
